// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Achievement Definitions
 * 
 * Categories:
 * - Exploration: Discovering systems, scanning, traveling
 * - Combat: Victories, kills, survival
 * - Trading: Credits earned, goods traded
 * - Progression: Upgrades, modules, ship improvements
 * - Story: Universe travel, special events
 * - Mastery: Expert-level achievements
 */

export const ACHIEVEMENT_CATEGORIES = {
  EXPLORATION: 'exploration',
  COMBAT: 'combat',
  TRADING: 'trading',
  PROGRESSION: 'progression',
  STORY: 'story',
  MASTERY: 'mastery'
};

export const ACHIEVEMENTS = {
  // ============================================================================
  // EXPLORATION ACHIEVEMENTS
  // ============================================================================
  
  first_steps: {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Visit your first star system',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '🚀',
    points: 10,
    hidden: false,
    requirement: { type: 'systems_visited', value: 1 }
  },
  
  explorer: {
    id: 'explorer',
    name: 'Explorer',
    description: 'Visit 10 different star systems',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '🌟',
    points: 25,
    hidden: false,
    requirement: { type: 'systems_visited', value: 10 }
  },
  
  veteran_explorer: {
    id: 'veteran_explorer',
    name: 'Veteran Explorer',
    description: 'Visit 50 different star systems',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '⭐',
    points: 50,
    hidden: false,
    requirement: { type: 'systems_visited', value: 50 }
  },
  
  master_explorer: {
    id: 'master_explorer',
    name: 'Master Explorer',
    description: 'Visit 100 different star systems',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '🌌',
    points: 100,
    hidden: false,
    requirement: { type: 'systems_visited', value: 100 }
  },
  
  first_scan: {
    id: 'first_scan',
    name: 'First Scan',
    description: 'Perform your first deep scan',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '🔍',
    points: 10,
    hidden: false,
    requirement: { type: 'scans_performed', value: 1 }
  },
  
  scanner: {
    id: 'scanner',
    name: 'Scanner',
    description: 'Perform 25 deep scans',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '📡',
    points: 25,
    hidden: false,
    requirement: { type: 'scans_performed', value: 25 }
  },
  
  light_years_traveled_100: {
    id: 'light_years_traveled_100',
    name: 'Space Traveler',
    description: 'Travel 100 light-years',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '🛸',
    points: 25,
    hidden: false,
    requirement: { type: 'light_years_traveled', value: 100 }
  },
  
  light_years_traveled_1000: {
    id: 'light_years_traveled_1000',
    name: 'Interstellar Voyager',
    description: 'Travel 1,000 light-years',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '🌠',
    points: 50,
    hidden: false,
    requirement: { type: 'light_years_traveled', value: 1000 }
  },
  
  alien_contact: {
    id: 'alien_contact',
    name: 'First Contact',
    description: 'Encounter your first alien species',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '👽',
    points: 15,
    hidden: false,
    requirement: { type: 'aliens_encountered', value: 1 }
  },
  
  xenobiologist: {
    id: 'xenobiologist',
    name: 'Xenobiologist',
    description: 'Encounter 25 different alien species',
    category: ACHIEVEMENT_CATEGORIES.EXPLORATION,
    icon: '🧬',
    points: 50,
    hidden: false,
    requirement: { type: 'aliens_encountered', value: 25 }
  },
  
  // ============================================================================
  // COMBAT ACHIEVEMENTS
  // ============================================================================
  
  first_blood: {
    id: 'first_blood',
    name: 'First Blood',
    description: 'Win your first combat encounter',
    category: ACHIEVEMENT_CATEGORIES.COMBAT,
    icon: '⚔️',
    points: 15,
    hidden: false,
    requirement: { type: 'combat_victories', value: 1 }
  },
  
  warrior: {
    id: 'warrior',
    name: 'Warrior',
    description: 'Win 10 combat encounters',
    category: ACHIEVEMENT_CATEGORIES.COMBAT,
    icon: '🗡️',
    points: 25,
    hidden: false,
    requirement: { type: 'combat_victories', value: 10 }
  },
  
  veteran_warrior: {
    id: 'veteran_warrior',
    name: 'Veteran Warrior',
    description: 'Win 50 combat encounters',
    category: ACHIEVEMENT_CATEGORIES.COMBAT,
    icon: '⚡',
    points: 50,
    hidden: false,
    requirement: { type: 'combat_victories', value: 50 }
  },
  
  ace_pilot: {
    id: 'ace_pilot',
    name: 'Ace Pilot',
    description: 'Achieve ACE status (3+ consecutive victories)',
    category: ACHIEVEMENT_CATEGORIES.COMBAT,
    icon: '🎖️',
    points: 30,
    hidden: false,
    requirement: { type: 'ace_status_achieved', value: 1 }
  },
  
  destroyer: {
    id: 'destroyer',
    name: 'Destroyer',
    description: 'Destroy 100 enemy ships',
    category: ACHIEVEMENT_CATEGORIES.COMBAT,
    icon: '💥',
    points: 50,
    hidden: false,
    requirement: { type: 'enemies_destroyed', value: 100 }
  },
  
  survivor: {
    id: 'survivor',
    name: 'Survivor',
    description: 'Survive a combat with less than 10% hull',
    category: ACHIEVEMENT_CATEGORIES.COMBAT,
    icon: '🛡️',
    points: 25,
    hidden: false,
    requirement: { type: 'survived_low_hull', value: 1 }
  },
  
  perfect_victory: {
    id: 'perfect_victory',
    name: 'Perfect Victory',
    description: 'Win a combat without taking damage',
    category: ACHIEVEMENT_CATEGORIES.COMBAT,
    icon: '✨',
    points: 30,
    hidden: false,
    requirement: { type: 'perfect_victories', value: 1 }
  },
  
  combo_master: {
    id: 'combo_master',
    name: 'Combo Master',
    description: 'Achieve a 10-hit combo in combat',
    category: ACHIEVEMENT_CATEGORIES.COMBAT,
    icon: '🔥',
    points: 35,
    hidden: false,
    requirement: { type: 'max_combo', value: 10 }
  },
  
  // ============================================================================
  // TRADING ACHIEVEMENTS
  // ============================================================================
  
  first_trade: {
    id: 'first_trade',
    name: 'First Trade',
    description: 'Complete your first trade',
    category: ACHIEVEMENT_CATEGORIES.TRADING,
    icon: '💰',
    points: 10,
    hidden: false,
    requirement: { type: 'trades_completed', value: 1 }
  },
  
  merchant: {
    id: 'merchant',
    name: 'Merchant',
    description: 'Complete 50 trades',
    category: ACHIEVEMENT_CATEGORIES.TRADING,
    icon: '🏪',
    points: 30,
    hidden: false,
    requirement: { type: 'trades_completed', value: 50 }
  },
  
  credits_10k: {
    id: 'credits_10k',
    name: 'Getting Rich',
    description: 'Accumulate 10,000 credits',
    category: ACHIEVEMENT_CATEGORIES.TRADING,
    icon: '💵',
    points: 20,
    hidden: false,
    requirement: { type: 'max_credits', value: 10000 }
  },
  
  credits_100k: {
    id: 'credits_100k',
    name: 'Wealthy Captain',
    description: 'Accumulate 100,000 credits',
    category: ACHIEVEMENT_CATEGORIES.TRADING,
    icon: '💎',
    points: 50,
    hidden: false,
    requirement: { type: 'max_credits', value: 100000 }
  },
  
  credits_1m: {
    id: 'credits_1m',
    name: 'Millionaire',
    description: 'Accumulate 1,000,000 credits',
    category: ACHIEVEMENT_CATEGORIES.TRADING,
    icon: '👑',
    points: 100,
    hidden: false,
    requirement: { type: 'max_credits', value: 1000000 }
  },
  
  profit_margin: {
    id: 'profit_margin',
    name: 'Profit Margin',
    description: 'Make 5,000 credits profit on a single trade',
    category: ACHIEVEMENT_CATEGORIES.TRADING,
    icon: '📈',
    points: 25,
    hidden: false,
    requirement: { type: 'max_trade_profit', value: 5000 }
  },
  
  // ============================================================================
  // PROGRESSION ACHIEVEMENTS
  // ============================================================================
  
  first_upgrade: {
    id: 'first_upgrade',
    name: 'First Upgrade',
    description: 'Purchase your first ship upgrade',
    category: ACHIEVEMENT_CATEGORIES.PROGRESSION,
    icon: '🔧',
    points: 10,
    hidden: false,
    requirement: { type: 'upgrades_purchased', value: 1 }
  },
  
  upgraded: {
    id: 'upgraded',
    name: 'Upgraded',
    description: 'Purchase 10 ship upgrades',
    category: ACHIEVEMENT_CATEGORIES.PROGRESSION,
    icon: '⚙️',
    points: 30,
    hidden: false,
    requirement: { type: 'upgrades_purchased', value: 10 }
  },
  
  fully_upgraded: {
    id: 'fully_upgraded',
    name: 'Fully Upgraded',
    description: 'Purchase all available upgrades',
    category: ACHIEVEMENT_CATEGORIES.PROGRESSION,
    icon: '🏆',
    points: 75,
    hidden: false,
    requirement: { type: 'all_upgrades_purchased', value: 1 }
  },
  
  module_collector: {
    id: 'module_collector',
    name: 'Module Collector',
    description: 'Install 5 different modules',
    category: ACHIEVEMENT_CATEGORIES.PROGRESSION,
    icon: '📦',
    points: 25,
    hidden: false,
    requirement: { type: 'modules_installed', value: 5 }
  },
  
  arsenal: {
    id: 'arsenal',
    name: 'Arsenal',
    description: 'Unlock all weapon slots',
    category: ACHIEVEMENT_CATEGORIES.PROGRESSION,
    icon: '🔫',
    points: 40,
    hidden: false,
    requirement: { type: 'all_weapon_slots_unlocked', value: 1 }
  },
  
  // ============================================================================
  // STORY ACHIEVEMENTS
  // ============================================================================
  
  universe_hopper: {
    id: 'universe_hopper',
    name: 'Universe Hopper',
    description: 'Travel through your first warp gate',
    category: ACHIEVEMENT_CATEGORIES.STORY,
    icon: '🌀',
    points: 20,
    hidden: false,
    requirement: { type: 'universes_visited', value: 2 }
  },
  
  multiverse_explorer: {
    id: 'multiverse_explorer',
    name: 'Multiverse Explorer',
    description: 'Visit 5 different universes',
    category: ACHIEVEMENT_CATEGORIES.STORY,
    icon: '🌐',
    points: 50,
    hidden: false,
    requirement: { type: 'universes_visited', value: 5 }
  },
  
  reality_bender: {
    id: 'reality_bender',
    name: 'Reality Bender',
    description: 'Visit 10 different universes',
    category: ACHIEVEMENT_CATEGORIES.STORY,
    icon: '🔮',
    points: 75,
    hidden: false,
    requirement: { type: 'universes_visited', value: 10 }
  },
  
  black_hole_observer: {
    id: 'black_hole_observer',
    name: 'Black Hole Observer',
    description: 'Discover a black hole',
    category: ACHIEVEMENT_CATEGORIES.STORY,
    icon: '⚫',
    points: 30,
    hidden: false,
    requirement: { type: 'black_holes_discovered', value: 1 }
  },
  
  // ============================================================================
  // MASTERY ACHIEVEMENTS
  // ============================================================================
  
  speed_demon: {
    id: 'speed_demon',
    name: 'Speed Demon',
    description: 'Travel at Warp 9',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '⚡',
    points: 25,
    hidden: false,
    requirement: { type: 'max_warp_factor', value: 9 }
  },
  
  fuel_efficient: {
    id: 'fuel_efficient',
    name: 'Fuel Efficient',
    description: 'Travel 100 LY using less than 50% fuel',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '⛽',
    points: 30,
    hidden: false,
    requirement: { type: 'efficient_travel', value: 1 }
  },
  
  completionist: {
    id: 'completionist',
    name: 'Completionist',
    description: 'Unlock all other achievements',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '🏅',
    points: 200,
    hidden: false,
    requirement: { type: 'all_achievements', value: 1 }
  },
  
  time_traveler: {
    id: 'time_traveler',
    name: 'Time Traveler',
    description: 'Play for 10 hours total',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '⏰',
    points: 50,
    hidden: false,
    requirement: { type: 'play_time_hours', value: 10 }
  },
  
  dedicated: {
    id: 'dedicated',
    name: 'Dedicated',
    description: 'Play for 50 hours total',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '⌛',
    points: 100,
    hidden: false,
    requirement: { type: 'play_time_hours', value: 50 }
  },
  
  // ============================================================================
  // HIDDEN/SECRET ACHIEVEMENTS
  // ============================================================================
  
  lucky_seven: {
    id: 'lucky_seven',
    name: 'Lucky Seven',
    description: 'Have exactly 7,777 credits',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '🎰',
    points: 25,
    hidden: true,
    requirement: { type: 'exact_credits', value: 7777 }
  },
  
  broke: {
    id: 'broke',
    name: 'Broke',
    description: 'Run out of credits completely',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '💸',
    points: 10,
    hidden: true,
    requirement: { type: 'exact_credits', value: 0 }
  },
  
  stranded: {
    id: 'stranded',
    name: 'Stranded',
    description: 'Run out of fuel in deep space',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '🆘',
    points: 15,
    hidden: true,
    requirement: { type: 'stranded', value: 1 }
  },
  
  near_death: {
    id: 'near_death',
    name: 'Near Death Experience',
    description: 'Survive with exactly 1% hull remaining',
    category: ACHIEVEMENT_CATEGORIES.MASTERY,
    icon: '💀',
    points: 20,
    hidden: true,
    requirement: { type: 'hull_at_1_percent', value: 1 }
  }
};

// Calculate total possible points
export const TOTAL_ACHIEVEMENT_POINTS = Object.values(ACHIEVEMENTS).reduce(
  (sum, achievement) => sum + achievement.points,
  0
);

// Get achievements by category
export const getAchievementsByCategory = (category) => {
  return Object.values(ACHIEVEMENTS).filter(a => a.category === category);
};

// Get visible achievements (not hidden)
export const getVisibleAchievements = () => {
  return Object.values(ACHIEVEMENTS).filter(a => !a.hidden);
};

// Get hidden achievements
export const getHiddenAchievements = () => {
  return Object.values(ACHIEVEMENTS).filter(a => a.hidden);
};

export default ACHIEVEMENTS;
